import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import posthog from 'posthog-js'
import { FiChevronLeft } from 'react-icons/fi'
import { ScoreFromNote, ScoreMeter, useScoreFrom } from 'components/memberScore'
import { getAdminMemberProfile, getMemberScore } from 'services/fund'
import { faFull, faPlain, longJalali } from 'utilities/helper/format'

const faRatio = new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 1 })

type TTone = 'plain' | 'danger' | 'warning' | 'success'

const TONES: Record<TTone, string> = {
    plain: ' bg-canvas text-text-body',
    danger: ' bg-danger-soft text-danger-on',
    warning: ' bg-warning-soft text-warning-on',
    success: ' bg-success-2 text-success-on',
}

function Tile({
    label,
    value,
    note,
    tone = 'plain',
}: {
    label: string
    value: string
    /** one line per entry — a number must never wrap away from its label */
    note?: string | string[]
    tone?: TTone
}) {
    const lines = note === undefined ? [] : ([] as string[]).concat(note)
    return (
        <div className={` min-w-0 rounded-[10px] p-2.5 ${TONES[tone]}`}>
            <p className=" text-[11px] opacity-80">{label}</p>
            <p className=" mt-1 text-sm font-bold tabular-nums">{value}</p>
            {lines.map((line) => (
                <p
                    key={line}
                    className=" mt-0.5 text-[10px] leading-5 opacity-80"
                >
                    {line}
                </p>
            ))}
        </div>
    )
}

/**
 * «مبلغ درخواست ۲٫۵ برابر سرمایه» — the one figure that ties the member's
 * record to THIS request. Many funds lend against a multiple of capital, and
 * without it a manager does the division in their head.
 */
const ratioNote = (requestAmount: number, capital: number) => {
    if (capital <= 0) return 'سرمایه‌ای در صندوق ندارد'
    const ratio = requestAmount / capital
    if (ratio < 1) return 'درخواست کمتر از سرمایه است'
    return `درخواست ${faRatio.format(ratio)} برابر سرمایه`
}

/**
 * کارنامهٔ عضو در یک نگاه — managers only, between the request and the vote.
 *
 * Owner's ask (2026-09-17): the facts that decide a loan, at the point of
 * deciding, with the full کارنامه one tap away. Before this a manager had to
 * leave the کارتابل, find the member in «اعضا», and come back — so the vote
 * was cast from memory.
 *
 * Four tiles, in the exact vocabulary of کارنامهٔ عضو's «وام‌ها و بازپرداخت»
 * (the owner already corrected those labels once), plus the score. Two
 * requests, deliberately independent: `member-profile` is a handful of
 * queries for one member, while the score's rank is a position in the WHOLE
 * fund and costs a full scan on a large one — so the tiles never wait for it.
 *
 * Facts only, like the grant wizard's panel: nothing here recommends or
 * blocks. A failed load still leaves the link to the full page.
 */
export default function MemberGlance({
    fundId,
    demandId,
    poolshipId,
    memberName,
    requestAmount,
}: {
    fundId: string
    demandId: string | undefined
    poolshipId: number
    memberName: string
    requestAmount: number
}) {
    const navigate = useNavigate()

    const profile = useQuery(
        ['admin-member-profile', fundId, poolshipId],
        getAdminMemberProfile,
        { staleTime: 60_000 }
    )
    const [scoreFrom] = useScoreFrom(fundId)
    const score = useQuery(
        ['member-score', fundId, poolshipId, scoreFrom],
        getMemberScore,
        {
            staleTime: 5 * 60 * 1000,
        }
    )

    const reported = useRef(false)
    useEffect(() => {
        if (reported.current) return
        if (profile.data) {
            reported.current = true
            posthog.capture('loan_demand_member_glance_viewed', {
                fund_id: fundId,
                demand_id: demandId,
                has_loans: profile.data.loans.count > 0,
                has_overdue: profile.data.repayment.overdue_count > 0,
            })
        } else if (profile.isError) {
            reported.current = true
            posthog.capture('loan_demand_member_glance_failed', {
                fund_id: fundId,
                demand_id: demandId,
                status: (profile.error as { response?: { status?: number } })
                    ?.response?.status,
            })
        }
    }, [profile.data, profile.isError, profile.error, fundId, demandId])

    const openRecord = () => {
        posthog.capture('loan_demand_member_record_opened', {
            fund_id: fundId,
            demand_id: demandId,
            poolship_id: poolshipId,
            glance_loaded: Boolean(profile.data),
        })
        navigate(`/fund/member-record/${fundId}/${poolshipId}`)
    }

    const data = profile.data
    const scoreRow = score.data?.result

    return (
        <section className=" mt-4 rounded-2xl border border-stroke bg-white p-4">
            <div className=" flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
                <p className=" text-sm font-bold text-heading">
                    کارنامهٔ {memberName}
                </p>
                {data?.joined_date && (
                    <p className=" text-[11px] text-text-secondary">
                        عضو از {longJalali(data.joined_date)}
                    </p>
                )}
            </div>

            {profile.isLoading && (
                <div
                    aria-label="در حال بارگذاری کارنامه"
                    className=" mt-3 grid animate-pulse grid-cols-2 gap-2"
                >
                    {[0, 1, 2, 3].map((key) => (
                        <div
                            key={key}
                            className=" h-[68px] rounded-[10px] bg-canvas"
                        />
                    ))}
                </div>
            )}

            {profile.isError && (
                <p className=" mt-2 text-[11px] leading-6 text-text-secondary">
                    خلاصهٔ کارنامه باز نشد؛ کارنامهٔ کامل را ببینید.
                </p>
            )}

            {data && (
                <>
                    {/* the score row reserves no space while it loads: on a
                        large fund it can arrive seconds after the tiles */}
                    {scoreRow && (
                        <div className=" mt-3">
                            <div className=" mb-1 flex items-baseline justify-between gap-2 text-[11px]">
                                <span className=" text-text-secondary">
                                    امتیاز عضو
                                </span>
                                <span className=" text-text-secondary">
                                    رتبهٔ {faPlain.format(scoreRow.rank)} از{' '}
                                    {faPlain.format(
                                        score.data?.members_count ?? 0
                                    )}
                                </span>
                            </div>
                            <ScoreMeter
                                percentile={scoreRow.percentile}
                                size="sm"
                            />
                            <ScoreFromNote
                                appliedFrom={score.data?.start_date}
                                className=" mt-1"
                            />
                        </div>
                    )}

                    <div className=" mt-3 grid grid-cols-2 gap-2">
                        <Tile
                            label="سرمایه"
                            value={`${faFull.format(data.capital)} تومان`}
                            note={ratioNote(requestAmount, data.capital)}
                        />
                        <Tile
                            label="مانده وام"
                            value={
                                data.loans.outstanding > 0
                                    ? `${faFull.format(
                                          data.loans.outstanding
                                      )} تومان`
                                    : 'ندارد'
                            }
                            note={
                                data.loans.count > 0
                                    ? [
                                          `${faPlain.format(
                                              data.loans.count
                                          )} وام تاکنون`,
                                          `مجموع ${faFull.format(
                                              data.loans.total_amount
                                          )}`,
                                      ]
                                    : 'تاکنون وامی نگرفته'
                            }
                        />
                        {data.repayment.overdue_amount > 0 ? (
                            <Tile
                                label="معوق"
                                tone="danger"
                                value={`${faFull.format(
                                    data.repayment.overdue_amount
                                )} تومان`}
                                note={`${faPlain.format(
                                    data.repayment.overdue_count
                                )} قسط، تا ${faPlain.format(
                                    data.repayment.max_days_overdue
                                )} روز`}
                            />
                        ) : data.repayment.matured_installments > 0 ? (
                            // Green only for a genuinely clean record. Seen
                            // live on the first day (request 20240): nothing
                            // overdue, but 0٪ on time and 356 days late on
                            // average — a green «ندارد» beside that reads as
                            // a good borrower. Current-but-late is not green.
                            <Tile
                                label="معوق"
                                tone={
                                    data.repayment.late_installments > 0
                                        ? 'plain'
                                        : 'success'
                                }
                                value="ندارد"
                                note="قسط معوقی ندارد"
                            />
                        ) : (
                            <Tile
                                label="معوق"
                                value="ندارد"
                                note="قسطی سررسید نشده"
                            />
                        )}
                        <Tile
                            label="نظم بازپرداخت"
                            // amber whenever an instalment was paid late OR is
                            // overdue — the same rule as the grant wizard's
                            // member panel. «۰٪ به‌موقع» must never be green.
                            tone={
                                data.repayment.on_time_rate === null
                                    ? 'plain'
                                    : data.repayment.late_installments > 0 ||
                                      data.repayment.overdue_count > 0
                                    ? 'warning'
                                    : 'success'
                            }
                            value={
                                data.repayment.on_time_rate === null
                                    ? 'سابقه‌ای ندارد'
                                    : `${faPlain.format(
                                          Math.round(
                                              data.repayment.on_time_rate
                                          )
                                      )}٪ به‌موقع`
                            }
                            note={
                                data.repayment.on_time_rate === null
                                    ? undefined
                                    : data.repayment.mean_days_late > 0
                                    ? `میانگین تأخیر ${faPlain.format(
                                          data.repayment.mean_days_late
                                      )} روز`
                                    : data.repayment.overdue_count > 0
                                    ? 'قسط سررسیده پرداخت نشده'
                                    : 'بدون تأخیر'
                            }
                        />
                    </div>

                    {data.portions.unpaid_amount > 0 && (
                        <p className=" mt-2 rounded-[10px] bg-danger-soft px-2.5 py-2 text-[11px] leading-5 text-danger-on">
                            حق عضویت معوق:{' '}
                            <span className=" font-bold tabular-nums">
                                {faFull.format(data.portions.unpaid_amount)}{' '}
                                تومان
                            </span>
                        </p>
                    )}
                </>
            )}

            <button
                type="button"
                onClick={openRecord}
                className=" mt-3 flex min-h-[44px] w-full items-center justify-between gap-2 rounded-[10px] bg-brand-soft px-3.5 text-xs font-bold text-brand-deep active:opacity-80"
            >
                مشاهدهٔ کارنامهٔ کامل
                <FiChevronLeft aria-hidden className=" h-4 w-4 shrink-0" />
            </button>
        </section>
    )
}
